import { action, runInAction } from 'mobx';
import { AxiosResponse } from 'axios';
import { notUndefined } from 'ui/base/types';
import { getResponseBytes, http, isHttpError } from 'services/base/http';
import { Host, HostThroughputData, IPangolinDomain, NetworkThroughputData, NetworkThroughputDuration } from './types';
import { GetNetworkThroughputResponse, NetworkThroughputObject } from './protos/network_pb';
import { ServiceStatus } from 'services/base/types';
import { GetHostThroughputResponse, HostThroughputObject } from './protos/hosts_pb';


type ThroughputSplit = {
  every: number;
  since: number;
};

const THROUGHPUT_SPLITS: Record<NetworkThroughputDuration, ThroughputSplit> = {
  [NetworkThroughputDuration.WEEKLY]: {
    every: 6 * 60 * 60 * 1000, // Six hours
    since: 7 * 24 * 60 * 60 * 1000, // One week
  },
  [NetworkThroughputDuration.DAILY]: {
    every: 60 * 60 * 1000, // One hour
    since: 24 * 60 * 60 * 1000, // One day
  },
  [NetworkThroughputDuration.HOURLY]: {
    every: 2 * 60 * 1000, // Two minutes
    since: 60 * 60 * 1000, // One hour
  },
};


export class ThroughputService {
  @action
  static clearAll(domain: IPangolinDomain) {
    domain.throughputEnabled = ServiceStatus.FRESH;
    domain.throughput = {
      [NetworkThroughputDuration.HOURLY]: { status: ServiceStatus.FRESH },
      [NetworkThroughputDuration.DAILY]: { status: ServiceStatus.FRESH },
      [NetworkThroughputDuration.WEEKLY]: { status: ServiceStatus.FRESH },
    };
  }

  static async fetchPangolinReport(domain: IPangolinDomain, duration: NetworkThroughputDuration): Promise<NetworkThroughputData[]> {
    let throughput: NetworkThroughputData[];
    try {
      domain.throughput[duration] = {
        status: ServiceStatus.LOADING,
      };
      const httpResp = await http.get(`/pangolin/${domain.id}/throughputs/${duration}`);
      const responseThroughput = parseNetworkThroughputResponse(httpResp);
      throughput = aggregateNetworkThroughputData(responseThroughput, duration);
      return runInAction(() => {
        // Success
        domain.throughputEnabled = ServiceStatus.LOADED;
        domain.throughput[duration] = {
          status: ServiceStatus.LOADED,
          data: throughput,
        };
        return throughput;
      });
    } catch (error) {
      return runInAction(() => {
        if (isHttpError(error)) {
          if (error.response?.status && error.response.status === 404) {
            // Silently fail on 404 because it means a pangolin has not updated
            domain.throughputEnabled = ServiceStatus.FAILED;
            throughput = aggregateNetworkThroughputData([], duration);
            domain.throughput[duration] = {
              status: ServiceStatus.LOADED,
              data: throughput,
            };
            return throughput;
          }
        }
        domain.throughput[duration] = {
          status: ServiceStatus.FAILED,
        };
        throw error;
      });
    }
  }

  static async fetchHostReport(domain: IPangolinDomain, host: Host, duration: NetworkThroughputDuration): Promise<HostThroughputData[]> {
    let throughput: HostThroughputData[];
    try {
      host.throughput[duration] = {
        status: ServiceStatus.LOADING,
      };
      const httpResp = await http.get(`/pangolin/${domain.id}/hosts/${host.id}/throughputs/${duration}`);
      const responseThroughput = parseHostThroughputResponse(httpResp);
      throughput = aggregateHostThroughputData(responseThroughput, duration);
      return runInAction(() => {
        // Success
        domain.throughputEnabled = ServiceStatus.LOADED;
        host.throughput[duration] = {
          status: ServiceStatus.LOADED,
          data: throughput,
        };
        return throughput;
      });
    } catch (error) {
      return runInAction(() => {
        if (isHttpError(error)) {
          if (error.response?.status && error.response.status === 404) {
            // Silently fail on 404 because it means a pangolin has not updated
            domain.throughputEnabled = ServiceStatus.FAILED;
            throughput = aggregateHostThroughputData([], duration);
            host.throughput[duration] = {
              status: ServiceStatus.LOADED,
              data: throughput,
            };
            return throughput;
          }
        }
        host.throughput[duration] = {
          status: ServiceStatus.FAILED,
        };
        throw error;
      });
    }
  }
}

function parseNetworkThroughputResponse(httpResp: AxiosResponse): NetworkThroughputData[] {
  const response = GetNetworkThroughputResponse.deserializeBinary(getResponseBytes(httpResp));
  const throughput = response.getThroughput().getItemsList().map(createNetworkThroughputData).filter(notUndefined);
  return throughput;
}

function createNetworkThroughputData(proto: NetworkThroughputObject): NetworkThroughputData | undefined {
  const start = proto.getTimeStart();
  const end = proto.getTimeEnd();
  if (start == null || end == null) {
    return undefined;
  }

  // Apparently unix timestmaps are always in utc
  return {
    start: new Date(start * 1000),
    end: new Date(end * 1000),
    egressPackets: proto.getEgrPkts() ?? 0,
    egressBytes: proto.getEgrBytes() ?? 0,
    ingressPackets: proto.getIngPkts() ?? 0,
    ingressBytes: proto.getIngBytes() ?? 0,
    internalPackets: proto.getIntPkts() ?? 0,
    internalBytes: proto.getIntBytes() ?? 0,
    allPackets: proto.getAllPkts() ?? 0,
    allBytes: proto.getAllBytes() ?? 0,
  };
}

function aggregateNetworkThroughputData(response: NetworkThroughputData[], duration: NetworkThroughputDuration): NetworkThroughputData[] {
  // Create entries manually and sum up the pangolin's responses into the constructed ones
  const { since, every } = THROUGHPUT_SPLITS[duration];
  const constructed: NetworkThroughputData[] = [];
  const now = Date.now();
  const start = now - since;
  for (let current = start; current < now; current += every) {
    constructed.push({
      start: new Date(current),
      end: new Date(current + every),
      egressPackets: 0,
      egressBytes: 0,
      ingressBytes: 0,
      ingressPackets: 0,
      internalPackets: 0,
      internalBytes: 0,
      allPackets: 0,
      allBytes: 0,
    });
  }

  response.forEach((data) => {
    const middle = new Date((data.start.getTime() + data.end.getTime()) / 2);
    const target = constructed.find((t) => {
      return t.start <= middle && middle <= t.end;
    });
    if (target == null) {
      return;
    }
    target.egressPackets += data.egressPackets;
    target.egressBytes += data.egressBytes;
    target.ingressPackets += data.ingressPackets;
    target.ingressBytes += data.ingressBytes;
    target.internalPackets += data.internalPackets;
    target.internalBytes += data.internalBytes;
    target.allPackets += data.allPackets;
    target.allBytes += data.allBytes;
  });

  return constructed;
}

function parseHostThroughputResponse(httpResp: AxiosResponse): HostThroughputData[] {
  const response = GetHostThroughputResponse.deserializeBinary(getResponseBytes(httpResp));
  const throughput = response.getThroughput().getItemsList().map(createHostThroughputData).filter(notUndefined);
  return throughput;
}

function createHostThroughputData(proto: HostThroughputObject): HostThroughputData | undefined {
  const start = proto.getTimeStart();
  const end = proto.getTimeEnd();
  if (start == null || end == null) {
    return undefined;
  }

  // Apparently unix timestmaps are always in utc
  return {
    start: new Date(start * 1000),
    end: new Date(end * 1000),

    externalTxPackets: proto.getExtTxPkts() ?? 0,
    externalTxBytes: proto.getExtTxBytes() ?? 0,
    externalRxPackets: proto.getExtRxPkts() ?? 0,
    externalRxBytes: proto.getExtRxBytes() ?? 0,

    internalTxPackets: proto.getIntTxPkts() ?? 0,
    internalTxBytes: proto.getIntTxBytes() ?? 0,
    internalRxPackets: proto.getIntRxPkts() ?? 0,
    internalRxBytes: proto.getIntRxBytes() ?? 0,

    allTxPackets: proto.getAllTxPkts() ?? 0,
    allTxBytes: proto.getAllTxBytes() ?? 0,
    allRxPackets: proto.getAllRxPkts() ?? 0,
    allRxBytes: proto.getAllRxBytes() ?? 0,
  };
}

function aggregateHostThroughputData(response: HostThroughputData[], duration: NetworkThroughputDuration): HostThroughputData[] {
  // Create entries manually and sum up the pangolin's responses into the constructed ones
  const { since, every } = THROUGHPUT_SPLITS[duration];
  const constructed: HostThroughputData[] = [];
  const now = Date.now();
  const start = now - since;
  for (let current = start; current < now; current += every) {
    constructed.push({
      start: new Date(current),
      end: new Date(current + every),
      externalTxPackets: 0,
      externalTxBytes: 0,
      externalRxPackets: 0,
      externalRxBytes: 0,

      internalTxPackets: 0,
      internalTxBytes: 0,
      internalRxPackets: 0,
      internalRxBytes: 0,

      allTxPackets: 0,
      allTxBytes: 0,
      allRxPackets: 0,
      allRxBytes: 0,
    });
  }

  response.forEach((data) => {
    const middle = new Date((data.start.getTime() + data.end.getTime()) / 2);
    const target = constructed.find((t) => {
      return t.start <= middle && middle <= t.end;
    });
    if (target == null) {
      return;
    }

    target.externalTxPackets += data.externalTxPackets;
    target.externalTxBytes += data.externalTxBytes;
    target.externalRxPackets += data.externalRxPackets;
    target.externalRxBytes += data.externalRxBytes;

    target.internalTxPackets += data.internalTxPackets;
    target.internalTxBytes += data.internalTxBytes;
    target.internalRxPackets += data.internalRxPackets;
    target.internalRxBytes += data.internalRxBytes;

    target.allTxPackets += data.allTxPackets;
    target.allTxBytes += data.allTxBytes;
    target.allRxPackets += data.allRxPackets;
    target.allRxBytes += data.allRxBytes;
  });

  return constructed;
}
